/**
 * The green programme's inputs, gathered once and shared: the Green screen
 * runs the engine on them, and the Benchmark reads the per-passenger figures.
 *
 * The platform's consumption endpoint is the source of the meter series.
 * Where it returns nothing — the sample does — the demonstration's annual
 * energy and water are spread over twelve months and marked as such, so the
 * screen shows the shape of the programme rather than a blank. Each series
 * says which it is, and the meter register says how many of its meters are
 * reporting, so a feed that is half connected reads as half connected.
 */
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useSession } from "../../app/SessionContext";
import type { SourceRef } from "../../domain/decision";
import {
  defaultMeasures,
  defaultPeers,
  runGreen,
  startingFactors,
  type ChecklistEvidence,
  type GreenEntered,
  type GreenInputs,
  type GreenOutput,
  type MonthPoint,
  type Peer,
} from "../../domain/green";
import type { DocumentRow } from "../../domain/library";
import { reporting, type MeterRegister } from "../../domain/meters";
import type { ConsumptionPoint } from "../../domain/testing";
import { useEntered } from "../executive/store";
import type { ExpansionEntered } from "../../domain/expansion";
import { t } from "../../i18n";

export interface GreenEnteredNote {
  entered?: GreenEntered;
  checklist?: Record<string, ChecklistEvidence>;
  peers?: Peer[];
  factors?: Partial<Record<keyof ReturnType<typeof startingFactors>, number>>;
  enteredBy?: string;
  enteredAt?: string;
}

/** Twelve months from the demonstration's annual totals, with a summer peak. Marked demo wherever shown. */
export function demoSeries(now: Date, annual: number, meters: number): MonthPoint[] {
  const shape = [0.075, 0.07, 0.072, 0.078, 0.088, 0.095, 0.1, 0.098, 0.09, 0.082, 0.077, 0.075];
  const out: MonthPoint[] = [];
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const month = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    out.push({ month, value: Math.round(annual * shape[d.getMonth()]), meters, readings: 30 });
  }
  return out;
}

/** Where one of the programme's series came from, said on the screen beside it. */
export interface SeriesSource {
  kind: "meters" | "demo";
  /** From the meter register: how many of this utility's meters are reporting now. */
  reporting: { live: number; total: number } | null;
  /** The readings' origins — a meter feed's connector, hand readings, a seeded history. */
  origins: string[];
  /** Only the billed supply was summed: the sub-meters beneath it apportion it, and counting them too would count it twice. */
  incomersOnly: boolean;
}

/**
 * One utility's monthly series from the platform's consumption per meter.
 *
 * Where the site marks an incomer — the meter the utility bills — only
 * incomers are summed. Sub-meters sit beneath an incomer and measure the same
 * energy again, so summing every meter of the type reported the terminal's
 * electricity once for the incomer and nearly once more for its sub-meters.
 * A site with no incomer marked has only its meters to go on, and they are
 * summed.
 */
export function meterSeries(
  points: readonly ConsumptionPoint[],
  register: MeterRegister | null,
  assetType: string,
): { series: MonthPoint[]; source: SeriesSource } {
  const incomers = points.filter((p) => p.role === "incomer");
  const used = incomers.length ? incomers : points;
  const byMonth = new Map<string, MonthPoint>();
  for (const p of used) {
    const m = p.period.slice(0, 7);
    const cur = byMonth.get(m) ?? { month: m, value: 0, meters: 0, readings: 0 };
    cur.value += p.consumed;
    cur.meters += 1;
    cur.readings = Math.max(cur.readings, p.readings);
    byMonth.set(m, cur);
  }
  const series = [...byMonth.values()].sort((a, b) => a.month.localeCompare(b.month));
  return {
    series,
    source: {
      kind: series.length ? "meters" : "demo",
      reporting: register && !register.demo ? reporting(register.meters, assetType) : null,
      origins: [...new Set(used.flatMap((p) => p.sources ?? []))].sort(),
      incomersOnly: incomers.length > 0 && incomers.length < points.length,
    },
  };
}

export function useGreen(): {
  out: GreenOutput | null;
  inputs: GreenInputs | null;
  note: ReturnType<typeof useEntered<GreenEnteredNote>>;
  demo: boolean;
  /** Per series: the airport's meters or the demonstration, and how many meters are reporting. */
  sources: { electricity: SeriesSource; water: SeriesSource } | null;
  pending: boolean;
} {
  const session = useSession();
  const source = session.source;
  const siteId = session.site.id;
  const records = useQuery({
    queryKey: ["green-records", siteId],
    staleTime: 60_000,
    queryFn: async () => {
      const quiet = async <T>(p: Promise<T>, fb: T) => p.catch(() => fb);
      const [kpis, documents, electricity, water, meters] = await Promise.all([
        quiet(source.listKpis(siteId), []),
        quiet(source.listDocuments({ siteId }), [] as DocumentRow[]),
        quiet(source.getConsumption(siteId, ["electricity_meter"], "month", 400), []),
        quiet(source.getConsumption(siteId, ["water_meter"], "month", 400), []),
        quiet<MeterRegister | null>(source.listMeters(siteId, ["electricity_meter", "water_meter"]), null),
      ]);
      return { kpis, documents, electricity, water, meters };
    },
  });
  const note = useEntered<GreenEnteredNote>("executive.green", {});
  /*
   * Build 7: the passengers, the waste tonnes and the diverted share were
   * filled with the demonstration's figures (9.2 million passengers) whenever
   * nobody had entered them — on the live platform too, where every
   * per-passenger figure then rested on a number the airport never gave. On
   * the platform they come from what was entered: here, else the passengers
   * entered on Capacity & Expansion; otherwise they are blank and the
   * programme says what is missing. The demo keeps its figures.
   */
  const platform = source.kind === "platform";
  const expansion = useEntered<ExpansionEntered>("executive.expansion", {});
  const expansionPax = expansion.value.passengersPerYear;
  const built = useMemo(() => {
    if (!records.data) return null;
    const now = new Date();
    const elec = meterSeries(records.data.electricity, records.data.meters, "electricity_meter");
    const wat = meterSeries(records.data.water, records.data.meters, "water_meter");
    const el = elec.series;
    const wt = wat.series;
    const demo = el.length === 0;
    const factors = startingFactors();
    for (const [k, v] of Object.entries(note.value.factors ?? {})) {
      const key = k as keyof typeof factors;
      if (v != null && factors[key])
        factors[key] = {
          ...factors[key],
          value: v,
          starting: false,
          source: `entered by ${note.value.enteredBy ?? "the airport"}`,
        };
    }
    const inputs: GreenInputs = {
      now,
      siteCode: session.site.code,
      kpis: records.data.kpis,
      electricity: demo ? demoSeries(now, 98_400_000, 4) : el,
      water: wt.length ? wt : demoSeries(now, 95_000, 2),
      factors,
      entered: {
        ...(platform
          ? expansionPax != null
            ? { passengersPerYear: expansionPax }
            : {}
          : { passengersPerYear: 9_200_000, wasteTonnesPerYear: 4_180, wasteDivertedPct: 44 }),
        ...note.value.entered,
      },
      checklist: note.value.checklist ?? {},
      peers: note.value.peers ?? defaultPeers(),
      measures: defaultMeasures(),
      documents: records.data.documents.map((d) => ({ id: d.id, title: d.title, tags: d.tags, status: d.status })),
    };
    return { inputs, out: runGreen(inputs), demo, sources: { electricity: elec.source, water: wat.source } };
  }, [records.data, note.value, session.site.code, platform, expansionPax]);
  return {
    out: built?.out ?? null,
    inputs: built?.inputs ?? null,
    note,
    demo: built?.demo ?? true,
    sources: built?.sources ?? null,
    pending: records.isPending,
  };
}

/** The per-passenger figures the Benchmark reads, with their sources. */
export function useGreenSummary(): {
  kgPerPax: number | null;
  kwhPerPax: number | null;
  acaLevel: number | null;
  sources: SourceRef[];
} | null {
  const g = useGreen();
  if (!g.out) return null;
  return {
    kgPerPax: g.out.intensity.kgPerPax.value,
    kwhPerPax: g.out.intensity.kwhPerPax.value,
    acaLevel: g.out.aca.level,
    sources: [
      g.demo
        ? { kind: "demo", ref: "demo energy feed", label: t("demonstration meter series") }
        : { kind: "ledger", ref: "meters", label: t("the platform's meters") },
    ],
  };
}
